import React, { useEffect, useState } from 'react'
import WidgetHeader from '../../../../components/Widget/Header'
import { FormattedDate } from 'react-intl'
import { useHistory } from 'react-router-dom'
import OrderScopeFilter, { readScope, writeScope } from '../OrderScopeFilter'

/**
 * Tasks on closed orders that the talent is owed money for but that Finance's
 * "To Be Paid" list can never show - so unless a coordinator fixes the task, the
 * talent is simply never paid.
 *
 * The reason column is the actionable part: "No file uploaded" needs the file
 * chased, "Not accepted by talent" needs the assignment sorted out. Both are
 * fixed on the task itself, which is what the row links to.
 *
 * @see documentation/UNPAID_TASK_CONTROLS.md
 */
const MissedPaymentTasksWidget = ({ widget }) => {
  const history = useHistory()

  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [scope, setScope] = useState(() => readScope(widget.widgetId))

  useEffect(() => {
    // Scoped to this run rather than a shared ref - see OrderListWidget: a
    // shared flag is true again by the time an abandoned request resolves, so
    // the stale list wins.
    let current = true
    setLoading(true)

    fetch(`coordinatordashboard/Missed_Payment_Tasks?scope=${scope}`)
      .then((response) => response.json())
      .then((body) => {
        if (!current) return
        setTasks((body.data && body.data.tasks) || [])
        setLoading(false)
      })
      .catch(() => {
        if (current) setLoading(false)
      })

    return () => {
      current = false
    }
  }, [scope])

  const changeScope = (next) => {
    writeScope(widget.widgetId, next)
    setScope(next)
  }

  const title = "Missed Payment Tasks"

  const formatCost = (task) =>
    `${Number(task.cost).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${task.cost_currency || ''}`.trim()

  return (
    <div className="bg-white border shadow rounded border-gray-400" id={`ID_${widget.widgetId}`}>
      <WidgetHeader
        widget={widget}
        widgetTitle={`${title}${tasks.length ? ` (${tasks.length})` : ''}`}
      />

      <OrderScopeFilter value={scope} onChange={changeScope} />

      {/* The 2026 horizon is stated because it is not obvious from an empty
          widget, and "nothing outstanding" would otherwise read as a clean bill
          of health while an older backlog sat unlisted. */}
      <p className="border-b bg-gray-50 px-3 py-2 text-xs text-gray-500">
        Closed orders from 2026 onwards with an unpaid task the talent can never be
        paid for. Finance's To&nbsp;Be&nbsp;Paid list does not show these.
      </p>

      <div className="w-full text-xs">
        {loading && <p className="p-4 text-center text-gray-400">Loading…</p>}

        {!loading && tasks.length === 0 && (
          <p className="p-4 text-center text-gray-400">Nothing outstanding.</p>
        )}

        {tasks.map((task) => (
          <div
            key={task.task_uid}
            className="flex cursor-pointer items-center border-b hover:bg-gray-200 hover:text-primary"
            onClick={() => history.push(`/task/${task.task_uid}`)}
            title={task.status_name}
          >
            <div className="w-16 shrink-0 px-3 py-2">{task.type_shortname || task.type_name}</div>

            <div className="grow truncate px-3 py-2">
              <span className="truncate">{task.task_title}</span>
              <span className="block truncate text-gray-500">
                {task.supplier_name} · {task.order_name}
              </span>
            </div>

            <div className="w-24 shrink-0 px-3 py-2 text-right">{formatCost(task)}</div>

            <div className="w-40 shrink-0 px-3 py-2">
              <span className="block text-red-600">{task.reason}</span>
              <span className="block text-gray-500">
                <FormattedDate value={new Date(task.task_created * 1000)} />
                {` · ${task.task_age_days}d`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MissedPaymentTasksWidget
